import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Logo } from "./logo";
import { serviceDetails } from "@/data/service-dropdown-content";
import { button3DClass } from "./button-3d";
import { useSession } from "@/hooks/use-session";
import { supabase } from "@/integrations/supabase/client";


const services: { slug: string; label: string }[] = [
  { slug: "seo", label: "SEO" },
  { slug: "aeo", label: "AEO (Answer Engine)" },
  { slug: "content", label: "Content Strategy" },
  { slug: "smo", label: "Social (SMO)" },
  { slug: "optimization", label: "Content Optimization" },
  { slug: "linkbuilding", label: "Link Building" },
];

const featuredSlugs = Object.keys(serviceDetails);

export function SiteNav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [activeService, setActiveService] = useState<string | null>("seo");
  const { user } = useSession();
  const navigate = useNavigate();

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate({ to: "/", replace: true });
  }

  const detail = activeService ? serviceDetails[activeService] : null;


  return (
    <nav className="sticky top-0 z-50 w-full border-b border-brand-deep/5 bg-white/85 backdrop-blur-md">
      <div className="mx-auto grid max-w-7xl grid-cols-2 items-center px-6 py-4 md:grid-cols-3">
        <Logo />
        <div className="hidden items-center justify-center gap-8 md:flex">
            <Link to="/about" className="rounded-md px-3 py-2 text-sm font-medium text-brand-deep/70 transition-colors hover:bg-brand-accent/10 hover:text-brand-accent">
              About
            </Link>
            <div className="group relative">
              <Link
                to="/services"
                className="flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-brand-deep/70 transition-colors hover:bg-brand-accent/10 hover:text-brand-accent"
              >
                Services
                <span className="text-[10px] opacity-50">▼</span>
              </Link>
              <div className="invisible absolute left-1/2 top-full w-[46rem] -translate-x-1/2 border border-brand-deep/5 bg-white p-0 opacity-0 shadow-xl transition-all duration-150 group-hover:visible group-hover:opacity-100">
                <div className="grid grid-cols-[1fr_1.35fr]">
                  {/* Links */}
                  <div className="p-4">
                    <div className="mb-2 px-3 text-[10px] font-bold uppercase tracking-widest text-brand-deep/80">
                      Services
                    </div>
                    {services.map((s) => (
                      <Link
                        key={s.slug}
                        to="/services/$slug"
                        params={{ slug: s.slug }}
                        onMouseEnter={() => setActiveService(s.slug)}
                        className="block rounded px-3 py-2 text-sm text-brand-deep/80 transition-colors hover:bg-brand-accent/10 hover:text-brand-accent"
                      >
                        {s.label}
                      </Link>
                    ))}
                    <div className="my-2 border-t border-brand-deep/5" />
                    <Link
                      to="/services"
                      className="block rounded px-3 py-2 text-xs font-semibold uppercase tracking-wider text-brand-accent hover:bg-brand-accent/10"
                    >
                      View all services →
                    </Link>
                  </div>

                  {/* Featured detail card — shown for SEO, AEO, or Content Strategy hover */}
                  {detail && (
                    <div className="bg-brand-muted p-5">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">Featured</div>
                      <h3 className="mt-2 font-serif text-xl">{detail.label}</h3>
                      <p className="mt-2 text-xs leading-relaxed text-brand-deep/70">{detail.whatIs}</p>
                      <div className="mt-3 space-y-2">
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">Why it matters</div>
                          <p className="mt-1 text-xs leading-relaxed text-brand-deep/70">{detail.whyItMatters}</p>
                        </div>
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">How you benefit</div>
                          <p className="mt-1 text-xs leading-relaxed text-brand-deep/70">{detail.howYouBenefit}</p>
                        </div>
                      </div>
                      <div className="mt-3">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">Key Offerings</div>
                        <ul className="mt-1 space-y-1 text-xs text-brand-deep/70">
                          {detail.offerings.map((item) => (
                            <li key={item} className="flex items-start gap-2">
                              <span className="mt-1 text-brand-accent">→</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      {detail.whatToExpect && detail.whatToExpect.length > 0 && (
                        <div className="mt-3">
                          <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">What to Expect</div>
                          <ul className="mt-1 space-y-1 text-xs text-brand-deep/70">
                            {detail.whatToExpect.map((item) => (
                              <li key={item} className="flex items-start gap-2">
                                <span className="mt-1 text-brand-accent">→</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="mt-3">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">How Customers Benefit</div>
                        <p className="mt-1 text-xs leading-relaxed text-brand-deep/70">{detail.howCustomersBenefit}</p>
                      </div>
                      <Link
                        to={detail.ctaLink}
                        className="mt-4 inline-block text-xs font-semibold text-brand-accent hover:text-brand-deep"
                      >
                        {detail.ctaLabel} →
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <Link to="/pricing" className="rounded-md px-3 py-2 text-sm font-medium text-brand-deep/70 transition-colors hover:bg-brand-accent/10 hover:text-brand-accent">
              Pricing
            </Link>
            <Link to="/blog" className="rounded-md px-3 py-2 text-sm font-medium text-brand-deep/70 transition-colors hover:bg-brand-accent/10 hover:text-brand-accent">
              Blog
            </Link>
            <div className="group relative">
              <button
                type="button"
                className="flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-brand-deep/70 transition-colors hover:bg-brand-accent/10 hover:text-brand-accent"
              >
                Tools
                <span className="text-[10px] opacity-50">▼</span>
              </button>
              <div className="invisible absolute left-1/2 top-full w-72 -translate-x-1/2 border border-brand-deep/5 bg-white p-3 opacity-0 shadow-xl transition-all duration-150 group-hover:visible group-hover:opacity-100">
                <Link
                  to="/tools/headline-analyzer"
                  className="group/tool flex items-center justify-between rounded-lg bg-brand-accent/5 p-3 transition-colors hover:bg-brand-accent/10"
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-brand-deep">Headline Analyzer</span>
                    <span className="text-xs text-brand-deep/60">Optimize your reach</span>
                  </div>
                  <span className="rounded-full bg-brand-accent px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">Free</span>
                </Link>
                <div className="my-2 border-t border-brand-deep/5" />
                <Link
                  to="/tools/keyword-explorer"
                  className="flex items-center justify-between rounded-lg bg-brand-accent/5 p-3 transition-colors hover:bg-brand-accent/10"
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-brand-deep">Keyword Explorer</span>
                    <span className="text-xs text-brand-deep/60">Find long-tail keywords</span>
                  </div>
                  <span className="rounded-full bg-brand-accent px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">Free</span>
                </Link>
                <div className="my-2 border-t border-brand-deep/5" />
                <Link
                  to="/tools/website-audit"
                  className="flex items-center justify-between rounded-lg bg-brand-accent/5 p-3 transition-colors hover:bg-brand-accent/10"
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-brand-deep">Website Audit</span>
                    <span className="text-xs text-brand-deep/60">Real Google speed &amp; SEO scores</span>
                  </div>
                  <span className="rounded-full bg-brand-accent px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">Free</span>
                </Link>
              </div>
            </div>
        </div>
        <div className="flex items-center justify-end gap-3">
          {user ? (
            <Link
              to="/account"
              className="hidden rounded-md px-3 py-2 text-sm font-semibold text-brand-deep/70 transition-colors hover:bg-brand-accent/10 hover:text-brand-accent sm:block"
            >
              My account
            </Link>
          ) : (
            <Link
              to="/auth"
              search={{ next: "/pricing" }}
              className="hidden rounded-md px-3 py-2 text-sm font-semibold text-brand-deep/70 transition-colors hover:bg-brand-accent/10 hover:text-brand-accent sm:block"
            >
              Log in
            </Link>
          )}

          <Link
            to="/pricing"
            className={button3DClass("accent", "sm")}
          >
            Get Started
          </Link>
          <button
            type="button"
            aria-label="Toggle menu"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden text-brand-deep"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="border-t border-brand-deep/5 bg-white md:hidden">
          <div className="mx-auto flex max-w-7xl flex-col px-6 py-4 text-sm font-medium">
            <Link to="/about" onClick={() => setMobileOpen(false)} className="py-2">About</Link>
            <Link to="/services" onClick={() => setMobileOpen(false)} className="py-2">Services</Link>
            <Link to="/pricing" onClick={() => setMobileOpen(false)} className="py-2">Pricing</Link>
            <Link to="/blog" onClick={() => setMobileOpen(false)} className="py-2">Blog</Link>
            <div className="py-2">
              <button
                type="button"
                onClick={() => setMobileToolsOpen((v) => !v)}
                className="flex w-full items-center justify-between text-left text-sm font-semibold text-brand-accent"
              >
                Tools
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-5 w-5 text-brand-deep/40 transition-transform ${mobileToolsOpen ? "rotate-180" : ""}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              {mobileToolsOpen && (
                <div className="mt-2 space-y-2 border-l-2 border-brand-accent/20 pl-4">
                  <Link
                    to="/tools/headline-analyzer"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-between rounded-xl bg-brand-accent/5 p-3"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-brand-deep">Headline Analyzer</span>
                      <span className="text-xs text-brand-deep/60">Optimize your reach</span>
                    </div>
                    <span className="rounded-full bg-brand-accent px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">Free</span>
                  </Link>
                  <Link
                    to="/tools/keyword-explorer"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-between rounded-xl bg-brand-accent/5 p-3"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-brand-deep">Keyword Explorer</span>
                      <span className="text-xs text-brand-deep/60">Find long-tail keywords</span>
                    </div>
                    <span className="rounded-full bg-brand-accent px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">Free</span>
                  </Link>
                  <Link
                    to="/tools/website-audit"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-between rounded-xl bg-brand-accent/5 p-3"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-brand-deep">Website Audit</span>
                      <span className="text-xs text-brand-deep/60">Real Google speed &amp; SEO scores</span>
                    </div>
                    <span className="rounded-full bg-brand-accent px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">Free</span>
                  </Link>
                </div>
              )}
            </div>
            {user ? (
              <>
                <Link to="/account" onClick={() => setMobileOpen(false)} className="py-2">My account</Link>
                <button type="button" onClick={() => { setMobileOpen(false); void handleSignOut(); }} className="py-2 text-left">Log out</button>
              </>
            ) : (
              <Link to="/auth" search={{ next: "/pricing" }} onClick={() => setMobileOpen(false)} className="py-2">Log in</Link>
            )}

          </div>
        </div>
      )}
    </nav>
  );
}
